import { z } from 'zod';
import { CargoProblemStatusSchema, CargoProblemTerminalOutcomeSchema } from './status.js';
import {
  CARGO_PROBLEM_COMMENT_MAX,
  CARGO_PROBLEM_COMMENT_PREVIEW_MAX,
  CARGO_PROBLEM_DESCRIPTION_MAX,
  CARGO_PROBLEM_PUBLIC_CODE_REGEX,
  CargoProblemCategorySchema,
  CargoProblemEventKindSchema,
  CargoProblemQueueSchema,
} from './vocab.js';

export const CargoProblemActorSchema = z
  .object({ id: z.string().uuid(), name: z.string() })
  .strict();
export type CargoProblemActor = z.infer<typeof CargoProblemActorSchema>;

export const CargoProblemCargoProjectionSchema = z
  .object({
    id: z.string().uuid(),
    article: z.string().nullable(),
    nameGoods: z.string().nullable(),
    transportationTariff: z.string().nullable(),
    weight: z.string().nullable(),
    volume: z.string().nullable(),
    carrier: z.string().nullable(),
  })
  .strict();
export type CargoProblemCargoProjection = z.infer<typeof CargoProblemCargoProjectionSchema>;

export const CargoProblemListItemSchema = z
  .object({
    id: z.string().uuid(),
    publicCode: z.string().regex(CARGO_PROBLEM_PUBLIC_CODE_REGEX),
    status: CargoProblemStatusSchema,
    category: CargoProblemCategorySchema,
    queue: CargoProblemQueueSchema,
    descriptionPreview: z.string().max(CARGO_PROBLEM_COMMENT_PREVIEW_MAX),
    author: CargoProblemActorSchema,
    assignee: CargoProblemActorSchema.nullable(),
    reactionDueAt: z.string().datetime().nullable(),
    overdue: z.boolean(),
    cargo: CargoProblemCargoProjectionSchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type CargoProblemListItem = z.infer<typeof CargoProblemListItemSchema>;

export const CargoProblemCapabilitiesSchema = z
  .object({
    canEdit: z.boolean(),
    canDelete: z.boolean(),
    canSubmit: z.boolean(),
    canReject: z.boolean(),
    canClaim: z.boolean(),
    canSubmitReview: z.boolean(),
    canEvaluate: z.boolean(),
    canComment: z.boolean(),
    canAttach: z.boolean(),
  })
  .strict();

export const CargoProblemDetailSchema = CargoProblemListItemSchema.extend({
  description: z.string().max(CARGO_PROBLEM_DESCRIPTION_MAX),
  capabilities: CargoProblemCapabilitiesSchema,
}).strict();
export type CargoProblemDetail = z.infer<typeof CargoProblemDetailSchema>;

export const CargoProblemCommentSchema = z
  .object({
    id: z.string().uuid(),
    author: CargoProblemActorSchema,
    body: z.string().max(CARGO_PROBLEM_COMMENT_MAX),
    createdAt: z.string().datetime(),
  })
  .strict();
export type CargoProblemComment = z.infer<typeof CargoProblemCommentSchema>;

export const CargoProblemRevisionReferenceSchema = z
  .object({
    id: z.string().uuid(),
    description: z.string().max(CARGO_PROBLEM_DESCRIPTION_MAX),
    category: CargoProblemCategorySchema,
    queue: CargoProblemQueueSchema,
    actor: CargoProblemActorSchema,
    createdAt: z.string().datetime(),
  })
  .strict();
export type CargoProblemRevisionReference = z.infer<typeof CargoProblemRevisionReferenceSchema>;

export const CargoProblemFileSchema = z
  .object({
    id: z.string().uuid(),
    fileName: z.string().min(1),
    contentType: z.string().min(1),
    contentLength: z.number().int().positive(),
    uploader: CargoProblemActorSchema,
    createdAt: z.string().datetime(),
  })
  .strict();
export type CargoProblemFile = z.infer<typeof CargoProblemFileSchema>;

export const CargoProblemActivityReferencesSchema = z
  .object({
    comment: z
      .object({
        id: z.string().uuid(),
        bodyPreview: z.string().max(CARGO_PROBLEM_COMMENT_PREVIEW_MAX),
      })
      .strict()
      .nullable(),
    file: CargoProblemFileSchema.nullable(),
    revision: CargoProblemRevisionReferenceSchema.nullable(),
  })
  .strict();

export const CargoProblemActivityMetadataSchema = z
  .object({
    from: CargoProblemStatusSchema.optional(),
    to: CargoProblemStatusSchema.optional(),
    outcome: CargoProblemTerminalOutcomeSchema.optional(),
    commentId: z.string().uuid().optional(),
    fileId: z.string().uuid().optional(),
    revisionId: z.string().uuid().optional(),
  })
  .strict();
export type CargoProblemActivityMetadata = z.infer<typeof CargoProblemActivityMetadataSchema>;

export const CargoProblemActivityItemSchema = z
  .object({
    id: z.string().uuid(),
    kind: CargoProblemEventKindSchema,
    actor: CargoProblemActorSchema,
    metadata: CargoProblemActivityMetadataSchema,
    references: CargoProblemActivityReferencesSchema,
    createdAt: z.string().datetime(),
  })
  .strict();
export type CargoProblemActivityItem = z.infer<typeof CargoProblemActivityItemSchema>;
